import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Zap, ShieldCheck } from 'lucide-react';
import HomeBackground from '../components/home/HomeBackground';
import HeroTradeCards from '../components/home/HeroTradeCards';
import HomePromo from '../components/home/HomePromo';
import ProfileCard from '../components/ProfileCard';
import StatsGrid from '../components/StatsGrid';
import TradingStrategy from '../components/TradingStrategy';
import TradeRecords from '../components/TradeRecords';
import FloatingActions from '../components/FloatingActions';
import './Home.css';
import '../styles/home-finance-components.css';
import '../styles/home-blue.css';

const TRUST_POINTS = [
  { icon: Zap, label: 'Sub-second updates' },
  { icon: ShieldCheck, label: 'Bank-grade security' },
  { icon: Sparkles, label: 'Curated by pros' },
];

const EASTERN_TZ = 'America/New_York';

const Home = () => {
  const [timeLabel, setTimeLabel] = useState('');

  useEffect(() => {
    const tick = () => {
      setTimeLabel(
        new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZone: EASTERN_TZ,
          timeZoneName: 'short',
        })
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="home home--premium premium-page">
      <HomeBackground />

      <div className="home__container">
        {/* ===== HERO ===== */}
        <section className="home__hero home-fade-in">
          <div className="home__hero-left">
            <p className="home__eyebrow">
              <Sparkles size={14} aria-hidden="true" />
              Real-Time Trading Intelligence
            </p>
            <h1 className="home__title">
              <span className="home__title-line">Master markets with</span>
              <span className="home__title-accent">institutional-grade signals</span>
            </h1>
            <p className="home__subtitle">
              Live trade blotter, curated research, and daily strategy — built for
              retail traders who want an unfair edge.
            </p>

            <div className="home__hero-cta">
              <Link to="/vip" className="home__btn home__btn--primary">
                Get VIP Access
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <Link to="/documents" className="home__btn home__btn--ghost">
                Explore Research
              </Link>
            </div>

            <div className="home__hero-trust">
              {TRUST_POINTS.map((t) => {
                const Ico = t.icon;
                return (
                  <span key={t.label} className="home__hero-trust-item">
                    <Ico size={13} aria-hidden="true" />
                    {t.label}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="home__hero-right">
            <HeroTradeCards />
            <div className="home__hero-meta">
              <div className="home__meta-row">
                <span className="home__meta-label">Feed</span>
                <span className="home__meta-value home__meta-value--live">Connected</span>
              </div>
              <div className="home__meta-row">
                <span className="home__meta-label">Clock</span>
                <span className="home__meta-value home__meta-value--clock">{timeLabel}</span>
              </div>
              <div className="home__meta-row">
                <span className="home__meta-label">Venues</span>
                <span className="home__meta-value">NASDAQ · NYSE · AMEX</span>
              </div>
            </div>
          </div>
        </section>

        {/* ===== COMPANY PROMO (video / images, admin editable) ===== */}
        <HomePromo />

        {/* ===== MENTOR / DASHBOARD ===== */}
        <section className="home__section home__section--mentor home-fade-in home-fade-in--delay-3">
          <div className="home__section-head">
            <div className="home__section-head-main">
              <span className="home__section-code">MENTOR</span>
              <div>
                <h2>Lead Analyst</h2>
                <p className="home__section-desc">Verified desk · live performance snapshot</p>
              </div>
            </div>
            <span className="home__section-meta">
              <span className="home__section-live-dot" aria-hidden="true" />
              Desk online
            </span>
          </div>
          <div className="home__dashboard">
            <ProfileCard />
            <StatsGrid />
          </div>
        </section>

        {/* ===== STRATEGY ===== */}
        <section className="home__section home-fade-in home-fade-in--delay-4">
          <div className="home__section-head">
            <div className="home__section-head-main">
              <span className="home__section-code">STRAT</span>
              <div>
                <h2>Daily Market Brief</h2>
                <p className="home__section-desc">Macro view · focus sectors · risk guidance</p>
              </div>
            </div>
            <span className="home__section-meta">
              <span className="home__section-live-dot" aria-hidden="true" />
              Intraday update
            </span>
          </div>
          <TradingStrategy />
        </section>

        {/* ===== BLOTTER ===== */}
        <section className="home__section home-fade-in home-fade-in--delay-5">
          <div className="home__section-head">
            <div className="home__section-head-main">
              <span className="home__section-code">BLOT</span>
              <div>
                <h2>Trade Blotter</h2>
                <p className="home__section-desc">Last 3 months · live quotes on open positions</p>
              </div>
            </div>
            <span className="home__section-meta">Filter · All / Active / Closed</span>
          </div>
          <TradeRecords />
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="home__cta-banner home-fade-in home-fade-in--delay-5">
          <div className="home__cta-banner__content">
            <p className="home__cta-banner__eyebrow">Ready to level up?</p>
            <h2 className="home__cta-banner__title">
              Join the <span className="home__title-accent">VIP desk</span> today
            </h2>
            <p className="home__cta-banner__desc">
              Access premium calls, live sessions, and priority research from the
              same institutional stack we use.
            </p>
          </div>
          <div className="home__cta-banner__actions">
            <Link to="/vip" className="home__btn home__btn--primary">
              Activate VIP
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
            <Link to="/documents" className="home__btn home__btn--ghost">
              Learn more
            </Link>
          </div>
        </section>
      </div>

      <FloatingActions />
    </div>
  );
};

export default Home;
